/* Čtení vyplněného hypotečního dotazníku ve složce klienta — po sekcích.
 *
 * ROZHODNUTÍ O RODNÉM ČÍSLE (a o všech citlivých polích):
 * Zobrazuje se MASKOVANĚ, plná hodnota jen po vědomém kliknutí, a sama se
 * po chvíli zase skryje. Důvody, proč to není zbytečná obstrukce:
 *
 *  1) Obrazovka poradce není soukromá. Na schůzce ji otáčí ke klientovi, na
 *     videohovoru ji sdílí, v kanceláři ji vidí kolegové. Rodné číslo je
 *     celostátní identifikátor — s ním a se jménem se dá jednat jménem klienta
 *     v registrech, u operátora i na úřadech. Nesmí svítit jen proto, že si
 *     poradce otevřel záložku, aby zkontroloval příjem.
 *  2) Poradce ho skoro nikdy nepotřebuje ČÍST. Potřebuje ho jednou PŘEPSAT do
 *     systému banky, což je vědomý úkon. Tomu odpovídá vědomé kliknutí.
 *  3) Minimalizace podle GDPR (čl. 5 odst. 1 písm. c a f) není o tom, kdo smí
 *     data vidět, ale kolikrát je opravdu vidí. Maskováním klesne počet
 *     zobrazení z „při každém otevření karty“ na „když je fakt potřebuju“.
 *  4) Vidět je datová část (prvních šest číslic = datum narození, které poradce
 *     zná). Skrytá je koncovka, tedy ta část, která z data narození dělá
 *     identifikátor. Poradce tak pozná, že pole je vyplněné a patří k tomuhle
 *     člověku, aniž by měl na stole použitelný údaj.
 *
 * Plná hodnota se NIKDY netahá dopředu „pro jistotu“ — o každou si komponenta
 * říká zvlášť, aby server věděl, že se na ni někdo díval.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Eye, EyeOff, Lock } from "lucide-react";
import { jeVyplneno, maskujCitlive, formatujCastku } from "./stavDotazniku";
import { relevantniPoleSekce, sekceRelevantni } from "@/lib/hypoteka/podminky";
import { useLanguage } from "@/contexts/LanguageContext";

/* PROČ `lang` ZŮSTÁVÁ PROPEM, KDYŽ UŽ TU JE useLanguage()
 * Prop `lang` řídí FORMÁT ČÍSEL (`formatujCastku`) a předává ho rodič.
 * Z kontextu se bere JEN `t`, tedy texty. Kdyby se formátování přepnulo na
 * kontext, změnilo by to chování u volajícího, který posílá jiný `lang` než
 * jaký má appka nastavený — a to není překlad, to je jiná věc. */

/* Za jak dlouho se odhalená hodnota sama schová. Dost dlouho na přepsání do
   banky, dost krátko na to, aby nezůstala na obrazovce do konce schůzky. */
const SKRYT_PO_MS = 20000;

const PRAZDNO = "—";

/** Hodnota jedné buňky tabulky — prázdno zůstává prázdnem, nikdy nulou. */
const bunka = (v, typ, lang) => (jeVyplneno(v) ? (formatujCastku(v, typ, lang) || String(v)) : PRAZDNO);

function Tabulka({ pole, radky, lang }) {
  const sloupce = Array.isArray(pole.sloupce) ? pole.sloupce : [];
  const data = Array.isArray(radky) ? radky.filter(Boolean) : [];
  if (!sloupce.length) return null;
  return (
    <div className="mt-2" data-testid={`dotaznik-tabulka-${pole.id}`}>
      <div className="text-popisek font-bold uppercase tracking-[0.06em] mb-1" style={{ color: "var(--muted)" }}>
        {pole.label}
      </div>
      {data.length === 0 ? (
        <div className="text-drobny" style={{ color: "var(--muted)" }}>{PRAZDNO}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-drobny">
            <thead>
              <tr>
                {sloupce.map((s) => (
                  <th key={s.id} className="text-left font-semibold pb-1 pr-3 whitespace-nowrap"
                    style={{ color: "var(--muted)", borderBottom: "1px solid var(--line)" }}>
                    {s.label || s.id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={i}>
                  {sloupce.map((s) => (
                    <td key={s.id} className="py-1 pr-3 align-top" style={{ borderBottom: "1px solid var(--line)" }}>
                      {bunka(r?.[s.id], s.typ, lang)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Jeden řádek: popisek vlevo, hodnota vpravo. U citlivého pole tlačítko oka. */
function Radek({ pole, hodnota, odhaleno, nacita, onOdhalit, onSkryt, lang }) {
  const { t } = useLanguage();
  const vyplneno = jeVyplneno(hodnota);
  const citlive = !!pole.citlive;

  /* Maskuje se VŽDY na frontendu, i když server posílá hodnoty maskované.
     Dvojitá pojistka: kdyby se nasadila starší nebo rozbitá verze routy,
     nesmí to skončit rodným číslem na obrazovce. */
  const zobrazit = !vyplneno
    ? PRAZDNO
    : citlive
      ? (odhaleno != null ? String(odhaleno) : (maskujCitlive(pole.id, hodnota) || PRAZDNO))
      : (formatujCastku(hodnota, pole.typ, lang) || String(hodnota));

  return (
    <div className="flex items-start gap-3 py-1.5" style={{ borderBottom: "1px solid var(--line)" }}>
      <div className="text-drobny flex-1 min-w-0" style={{ color: "var(--muted)" }}>
        {pole.label}
        {citlive && <Lock size={9} className="inline ml-1 -mt-0.5" aria-hidden="true" />}
      </div>
      <div className="text-[12px] font-semibold text-right break-words" style={{ color: vyplneno ? "var(--ink)" : "var(--muted)", maxWidth: "52%" }}>
        <span data-testid={`dotaznik-hodnota-${pole.id}`}>{zobrazit}</span>
        {citlive && vyplneno && (
          odhaleno != null ? (
            <button
              type="button"
              onClick={onSkryt}
              data-testid={`dotaznik-skryt-${pole.id}`}
              className="ml-2 inline-flex items-center gap-1 text-drobny font-semibold align-middle"
              style={{ color: "var(--muted)" }}
            >
              <EyeOff size={11} /> {t("auto.dotaznikCteni.skryt", "Skrýt")}
            </button>
          ) : (
            <button
              type="button"
              onClick={onOdhalit}
              disabled={nacita}
              data-testid={`dotaznik-odhalit-${pole.id}`}
              title={t("auto.dotaznikCteni.zobraziSeNa20Sekund", "Zobrazí se na 20 sekund")}
              className="ml-2 inline-flex items-center gap-1 text-drobny font-semibold align-middle"
              style={{ color: "var(--accent)" }}
            >
              <Eye size={11} /> {nacita ? "…" : t("auto.dotaznikCteni.zobrazit", "Zobrazit")}
            </button>
          )
        )}
      </div>
    </div>
  );
}

function Sekce({ sekce, odpovedi, otevrena, onPrepnout, odhalene, nacitane, onOdhalit, onSkryt, lang }) {
  /* Kreslí a počítá se JEN to, na co se dotazník při těchhle odpovědích ptá.
     Sólo žadatel by jinak měl u sekce Spolužadatel navždy „0 / 16“ a poradce
     by nepoznal, jestli klient sekci přeskočil úmyslně, nebo ji nedodělal.
     Podmínky vyhodnocuje sdílený modul; jejich SEZNAM je na serveru. */
  const pole = useMemo(() => relevantniPoleSekce(sekce, odpovedi), [sekce, odpovedi]);

  /* Počítá se ze skutečných odpovědí, ne z ničeho odhadnutého. U citlivých polí
     stačí vědět, že hodnota (byť maskovaná) dorazila — obsah k tomu netřeba. */
  const vyplnenych = pole.filter((p) => (p.typ === "tabulka"
    ? Array.isArray(odpovedi?.[p.id]) && odpovedi[p.id].length > 0
    : jeVyplneno(odpovedi?.[p.id]))).length;

  const skalary = pole.filter((p) => p.typ !== "tabulka");
  const tabulky = pole.filter((p) => p.typ === "tabulka");
  const Sipka = otevrena ? ChevronDown : ChevronRight;

  return (
    <section className="overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14 }}>
      <button
        type="button"
        onClick={onPrepnout}
        data-testid={`dotaznik-sekce-${sekce.id}`}
        aria-expanded={otevrena}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        <Sipka size={14} style={{ color: "var(--muted)" }} />
        <span className="text-[12px] font-bold flex-1" style={{ color: "var(--ink)" }}>{sekce.nazev}</span>
        <span className="text-drobny tabular-nums" style={{ color: "var(--muted)" }}
          data-testid={`dotaznik-sekce-pocet-${sekce.id}`}>
          {vyplnenych} / {pole.length}
        </span>
      </button>
      {otevrena && (
        <div className="px-3 pb-3" data-testid={`dotaznik-sekce-obsah-${sekce.id}`}>
          {skalary.map((p) => (
            <Radek
              key={p.id}
              pole={p}
              hodnota={odpovedi?.[p.id]}
              odhaleno={odhalene?.[p.id]}
              nacita={!!nacitane?.[p.id]}
              onOdhalit={() => onOdhalit(p.id)}
              onSkryt={() => onSkryt(p.id)}
              lang={lang}
            />
          ))}
          {tabulky.map((p) => <Tabulka key={p.id} pole={p} radky={odpovedi?.[p.id]} lang={lang} />)}
        </div>
      )}
    </section>
  );
}

/**
 * @param {Array}    sekce      definice sekcí ze serveru
 * @param {object}   odpovedi   odpovědi klienta (citlivá pole maskovaná)
 * @param {Function} onOdhalit  async (poleId) => plná hodnota; bez ní se nic neodhaluje
 * @param {string}   lang
 */
export default function DotaznikCteni({ sekce = [], odpovedi = {}, onOdhalit, lang = "cs" }) {
  const { t } = useLanguage();
  /* Sekce, na které se dotazník u tohohle klienta vůbec neptal, se nekreslí.
     Prázdná sekce Spolužadatel u sólo žadatele není informace — je to šum,
     ve kterém se ztratí to podstatné. */
  const seznam = (Array.isArray(sekce) ? sekce.filter(Boolean) : [])
    .filter((s) => sekceRelevantni(s, odpovedi));
  /* Sekce začínají zavřené: devadesát polí naráz nikdo nečte a zavřená sekce
     navíc znamená, že se citlivá pole ani nemaskovaně nekreslí na obrazovku. */
  const [otevrene, setOtevrene] = useState({});
  const [odhalene, setOdhalene] = useState({});
  const [nacitane, setNacitane] = useState({});
  const [chyba, setChyba] = useState("");
  const casovace = useRef({});

  /* Odhalené hodnoty musí zmizet i při odchodu ze záložky — jinak by zůstaly
     viset v paměti komponenty a s ní i v React devtools. */
  useEffect(() => {
    const t = casovace.current;
    return () => { Object.values(t).forEach(clearTimeout); };
  }, []);

  const skryt = (poleId) => {
    clearTimeout(casovace.current[poleId]);
    delete casovace.current[poleId];
    setOdhalene((s) => { const n = { ...s }; delete n[poleId]; return n; });
  };

  const odhal = async (poleId) => {
    if (typeof onOdhalit !== "function") return;
    setChyba("");
    setNacitane((s) => ({ ...s, [poleId]: true }));
    try {
      const plna = await onOdhalit(poleId);
      /* Prázdná odpověď se NEUKÁŽE jako odhalená hodnota — to by tvrdilo, že
         klient pole nevyplnil, ačkoliv se jen nepodařilo hodnotu načíst. */
      if (!jeVyplneno(plna)) {
        setChyba(t("auto.dotaznikCteni.hodnotuSeNepodariloNacist", "Hodnotu se nepodařilo načíst."));
        return;
      }
      setOdhalene((s) => ({ ...s, [poleId]: plna }));
      casovace.current[poleId] = setTimeout(() => skryt(poleId), SKRYT_PO_MS);
    } catch (e) {
      setChyba(t("auto.dotaznikCteni.hodnotuSeNepodariloNacist", "Hodnotu se nepodařilo načíst."));
    } finally {
      setNacitane((s) => { const n = { ...s }; delete n[poleId]; return n; });
    }
  };

  if (!seznam.length) {
    return (
      <div className="text-drobny py-4" style={{ color: "var(--muted)" }} data-testid="dotaznik-cteni-prazdno">
        {t("auto.dotaznikCteni.definiceDotaznikuSeNenacetla", "Definice dotazníku se nenačetla.")}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="dotaznik-cteni">
      {chyba && (
        <div className="text-drobny px-3 py-2 rounded-lg" data-testid="dotaznik-odhalit-chyba"
          style={{ background: "rgba(197,48,48,0.08)", color: "var(--negative-text)" }}>{chyba}</div>
      )}
      {seznam.map((s) => (
        <Sekce
          key={s.id}
          sekce={s}
          odpovedi={odpovedi}
          otevrena={!!otevrene[s.id]}
          onPrepnout={() => setOtevrene((o) => ({ ...o, [s.id]: !o[s.id] }))}
          odhalene={odhalene}
          nacitane={nacitane}
          onOdhalit={odhal}
          onSkryt={skryt}
          lang={lang}
        />
      ))}
      <p className="text-drobny pt-1" style={{ color: "var(--muted)" }}>
        {t(
          "auto.dotaznikCteni.rodneCisloACislaDokladu",
          "Rodné číslo a čísla dokladů jsou skryté. Zobrazení je vědomý krok a po 20 sekundách se hodnota zase schová."
        )}
      </p>
    </div>
  );
}
